// Fetch de plantillas (templates + categorías) desde Supabase para
// Interfaz V2. Mapea las filas crudas al shape que la UI ya consume
// (CollectionV2 / PresetV2) — así el resto de los componentes no
// cambian, solo el origen de los datos.
//
// Reglas de filtrado:
//   • Solo plantillas activas (`activo = true`).
//   • Solo de tipo imagen (`output_type IN 'imagen', 'imagen_pro'`).
//     La UI actual no soporta video; cuando se agregue, levantar este filtro.
//   • Globales (`id_company IS NULL`) + las de la empresa del user.
//   • Por rubro: si el template tiene `rubros = '{}'` se considera "para
//     todos los rubros"; sino, debe contener el rubro del user.
//   • Categorías vacías (sin templates que pasen los filtros) NO se
//     renderizan en la home — no tendría sentido mostrar una fila vacía.

import { supabase } from '../../lib/supabase';
import { SUPABASE_URL } from '../../config';
import type { CollectionV2, PresetV2 } from './mockData';

const TEMPLATES_BUCKET = 'templates';

/** Convierte un path del bucket en URL pública completa. */
export const templatePreviewUrl = (path: string | null | undefined): string => {
  if (!path) return '';
  return `${SUPABASE_URL}/storage/v1/object/public/${TEMPLATES_BUCKET}/${path}`;
};

export interface FetchTemplatesArgs {
  /** Rubro del user (de `company_profiles.rubro`). null = sin rubro definido. */
  rubro: string | null;
  /** id_company del user. null = sin empresa (solo se ven globales). */
  companyId: string | null;
}

interface RawTemplate {
  id: string;
  categoria_id: string;
  nombre: string;
  descripcion: string | null;
  preview_opt_path: string | null;
  preview_full_path: string | null;
  output_type: string;
  rubros: string[] | null;
  tipos_producto_sugeridos: string[] | null;
  inputs_aceptados: string[] | null;
  creditos_por_unidad: number | string | null;
  unidad_consumo: string | null;
  duration_seconds: number | string | null;
  id_company: string | null;
  orden: number;
}

interface RawCategoria {
  id: string;
  nombre: string;
  slug: string;
  descripcion: string | null;
  orden: number;
}

/**
 * Devuelve las plantillas agrupadas por categoría, listas para renderizar
 * como CollectionV2[]. El `id` de cada colección es el `slug` de la
 * categoría — usado en la URL `/interfaz-v2/coleccion/:slug`.
 */
export async function fetchTemplatesV2({
  rubro,
  companyId,
}: FetchTemplatesArgs): Promise<CollectionV2[]> {
  // 1. Categorías activas, ordenadas.
  const { data: categoriasRaw, error: catErr } = await supabase
    .from('template_categorias')
    .select('id, nombre, slug, descripcion, orden')
    .eq('activo', true)
    .order('orden', { ascending: true });
  if (catErr || !categoriasRaw) {
    console.error('[templatesApi] error fetching categorias', catErr);
    return [];
  }
  const categorias = categoriasRaw as RawCategoria[];

  // 2. Templates: activos + tipo imagen + visibles para el user.
  // Usamos `.or()` solo si companyId existe; sino, solo globales.
  let templatesQuery = supabase
    .from('templates')
    .select(
      'id, categoria_id, nombre, descripcion, preview_opt_path, preview_full_path, output_type, rubros, tipos_producto_sugeridos, inputs_aceptados, creditos_por_unidad, unidad_consumo, duration_seconds, id_company, orden',
    )
    .eq('activo', true)
    .in('output_type', ['imagen', 'imagen_pro', 'video'])
    .order('orden', { ascending: true });
  if (companyId) {
    templatesQuery = templatesQuery.or(
      `id_company.is.null,id_company.eq.${companyId}`,
    );
  } else {
    templatesQuery = templatesQuery.is('id_company', null);
  }
  const { data: templatesRaw, error: tplErr } = await templatesQuery;
  if (tplErr || !templatesRaw) {
    console.error('[templatesApi] error fetching templates', tplErr);
    return [];
  }
  const templates = templatesRaw as RawTemplate[];

  // 3. Filtro de rubro en cliente — más simple que armar el `.or()` con
  // sintaxis de contains en PostgREST (especialmente si el rubro tiene
  // caracteres raros). El dataset es chico, costo despreciable.
  const matchesRubro = (t: RawTemplate): boolean => {
    const rs = t.rubros || [];
    if (rs.length === 0) return true; // "para todos los rubros"
    return rubro ? rs.includes(rubro) : false;
  };
  const filtered = templates.filter(matchesRubro);

  // 4. Group by categoria_id.
  const byCategoria = new Map<string, RawTemplate[]>();
  for (const t of filtered) {
    const arr = byCategoria.get(t.categoria_id) || [];
    arr.push(t);
    byCategoria.set(t.categoria_id, arr);
  }

  // 5. Mapear a CollectionV2[] respetando el orden de categorías.
  const collections: CollectionV2[] = [];
  for (const cat of categorias) {
    const cats = byCategoria.get(cat.id);
    if (!cats || cats.length === 0) continue; // categoría vacía → skip
    collections.push({
      id: cat.slug, // usar slug para URLs
      title: cat.nombre,
      subtitle: cat.descripcion || undefined,
      presets: cats.map(
        (t): PresetV2 => ({
          id: t.id,
          title: t.nombre,
          description: t.descripcion || undefined,
          // opt → thumbnails (home grids, carruseles); full → previews
          // grandes (sheet, "Utilizar" carousel). Si full falta, los
          // componentes fallback a imageUrl (opt).
          imageUrl: templatePreviewUrl(t.preview_opt_path),
          imageUrlFull: t.preview_full_path
            ? templatePreviewUrl(t.preview_full_path)
            : undefined,
          kind: 'template',
          tiposProductoSugeridos: Array.isArray(t.tipos_producto_sugeridos)
            ? t.tipos_producto_sugeridos
            : undefined,
          inputsAceptados: Array.isArray(t.inputs_aceptados)
            ? t.inputs_aceptados
            : undefined,
          outputType:
            t.output_type === 'video'
              ? 'video'
              : t.output_type === 'imagen_pro'
                ? 'imagen_pro'
                : 'imagen',
          creditosPorUnidad:
            t.creditos_por_unidad != null
              ? Number(t.creditos_por_unidad)
              : undefined,
          unidadConsumo:
            t.unidad_consumo === 'segundo'
              ? 'segundo'
              : t.unidad_consumo === 'imagen'
                ? 'imagen'
                : t.unidad_consumo === 'generacion'
                  ? 'generacion'
                  : undefined,
          durationSeconds:
            t.duration_seconds != null
              ? Number(t.duration_seconds)
              : undefined,
        }),
      ),
    });
  }
  return collections;
}
